import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  Space,
  message,
} from "antd";
import * as Icons from "@ant-design/icons";
import {
  getConfigurations,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
} from "../../api/setting/System";

const sessiData = JSON.parse(sessionStorage.getItem("user"));
const ConfigurationPage = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<Icons.SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            handleReset(clearFilters);
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icons.SearchOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: "#ffc069", padding: 0 }}>{text}</span>
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
  };

  const columns = [
    {
      title: "Kode",
      dataIndex: "cfg_code",
      key: "cfg_code",
      ...getColumnSearchProps("cfg_code"),
    },
    {
      title: "Value",
      dataIndex: "cfg_value",
      key: "cfg_value",
      render: (cfg_value) => (
        <div style={{ paddingBottom: 8, display: "flex", flexWrap: "wrap" }}>
          {cfg_value
            .split(",")
            .map((val) => val.trim())
            .filter((val) => val)
            .map((val, i) => (
              <Tag key={i} style={{ marginBottom: 4, marginRight: 4 }}>
                {val}
              </Tag>
            ))}
        </div>
      ),
    },
    {
      title: "Deskripsi",
      dataIndex: "cfg_desc",
      key: "cfg_desc",
    },
    {
      title: "Remark",
      dataIndex: "cfg_remark",
      key: "cfg_remark",
    },
    {
      title: "Urutan",
      dataIndex: "cfg_ordinal",
      key: "cfg_ordinal",
      sorter: (a, b) => a.cfg_ordinal - b.cfg_ordinal,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "cfg_status",
      key: "cfg_status",
      filters: [
        { text: "Aktif", value: "Aktif" },
        { text: "Tidak Aktif", value: "Tidak Aktif" },
      ],
      onFilter: (value, record) => record.cfg_status === value,
      render: (cfg_status) =>
        cfg_status === "Aktif" ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
    },
    {
      title: "Aksi",
      key: "aksi",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button
            type="link"
            danger
            onClick={() => confirmDelete(record.cfg_id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    getDataConfig();
  }, []);

  const getDataConfig = async () => {
    setLoading(true);
    const configData = await getConfigurations()
      .then((res) => {
        setData(res.data.data);
      })
      .catch(() => {
        message.error("Gagal mengambil data konfigurasi.");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    form.setFieldsValue({ ...record });
    setIsModalOpen(true);
  };

  const handleModalOk = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          ...values,
          cfg_value: values.cfg_value.trim(),
          cfg_status: values.cfg_status ? "Aktif" : "Tidak Aktif",
          cfg_created_by: sessiData[0].user[0].fullname,
        };

        setLoading(true);

        const request = editingConfig
          ? updateConfiguration(editingConfig.cfg_id, payload)
          : createConfiguration(payload);

        request
          .then((res) => {
            if (editingConfig) {
              if (res.data.status) {
                getDataConfig();
                message.success(res.data.message);
              } else {
                message.error(res.data.message);
              }
            } else {
              if (res.data.status) {
                getDataConfig();
                message.success(res.data.message);
              } else {
                message.error(res.data.message);
              }
            }

            setIsModalOpen(false);
            form.resetFields();
          })
          .catch((error) => {
            const errorMessage =
              error?.response?.data?.message || "Gagal menyimpan konfigurasi.";
            message.error(errorMessage);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((errorInfo) => {
        // Tangani error validasi di sini
        console.error("Validasi gagal:", errorInfo);
        message.error("Harap lengkapi semua field yang wajib diisi.");
      });
  };

  const confirmDelete = (id) => {
    Modal.confirm({
      title: "Apakah Anda yakin ingin menghapus konfigurasi ini?",
      okText: "Ya, Hapus",
      okType: "danger",
      cancelText: "Batal",
      onOk: () => {
        setLoading(true);
        deleteConfiguration(id)
          .then((res) => {
            if (res.data.status) {
              message.success(res.data.message);
            } else {
              message.error(res.data.message);
            }
            getDataConfig();
          })
          .catch(() => {
            message.error("Gagal menghapus konfigurasi.");
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }}>
        + Tambah Configuration
      </Button>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="cfg_id"
        loading={loading}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
      />

      <Modal
        title={editingConfig ? "Edit Configuration" : "Tambah Configuration"}
        visible={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        okText="Simpan"
        cancelText="Batal"
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="cfg_code"
            label="Kode"
            rules={[{ required: true, message: "Kode wajib diisi" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="cfg_value"
            label="Value (pisahkan dengan koma)"
            rules={[{ required: true }]}
          >
            <Input placeholder="contoh: item1, item2, item3" />
          </Form.Item>
          <Form.Item name="cfg_desc" label="Deskripsi">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="cfg_remark" label="Remark">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="cfg_ordinal" label="Urutan">
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="cfg_status"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Tidak Aktif" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfigurationPage;
